import { Box, Button, Link, styled } from "@mui/material";
import { ExternalLink, FileText, LucideIcon, Plus } from "lucide-react";
import MarkdownComponent from "markdown-to-jsx";
import React from "react";

/**
 * Modal configuration map - single source of truth for modal icons.
 * When adding a new modal action, add it here with its icon.
 */
const MODAL_CONFIG: Record<string, { icon: LucideIcon }> = {
    "create-scope": { icon: Plus },
};

/**
 * Link protocol types for the unified link system.
 * Protocol determines rendering style, action determines behavior.
 */
type LinkProtocol = "button" | "link";
type LinkAction = "navigate" | "modal" | "source";

interface ParsedLink {
    protocol: LinkProtocol;
    action: LinkAction;
    target: string;
    params?: Record<string, string>; // e.g., { page: "4" }
}

/**
 * Parse a button:// or link:// URL into protocol, action, target, and params.
 *
 * Formats:
 *   button://navigate/PATH     → Button that opens /organizations/PATH in new tab
 *   button://modal/NAME        → Button that opens modal dialog
 *   button://source/DOC_ID?page=N → Button that opens source modal
 *   link://navigate/PATH       → Inline link that opens in new tab
 *   link://modal/NAME          → Inline link that opens modal
 *   link://source/DOC_ID?page=N → Inline link that opens source modal
 */
const parseLink = (href: string): ParsedLink | null => {
    // Match button:// or link://
    const protocolMatch = href.match(/^(button|link):\/\//);
    if (!protocolMatch) return null;

    const protocol = protocolMatch[1] as LinkProtocol;
    const withoutProtocol = href.replace(`${protocol}://`, "");

    // Extract action type and target
    const [action, ...rest] = withoutProtocol.split("/");
    if (!["navigate", "modal", "source"].includes(action)) return null;

    // Handle query params (for source)
    const targetWithParams = rest.join("/");
    const [target, queryString] = targetWithParams.split("?");

    // Target is required
    if (!target) return null;

    const params: Record<string, string> = {};
    if (queryString) {
        new URLSearchParams(queryString).forEach((v, k) => (params[k] = v));
    }

    return { protocol, action: action as LinkAction, target, params };
};

/**
 * Custom link component that handles button:// and link:// protocol links.
 *
 * Protocol determines rendering:
 *   button:// → Standalone button (block-level)
 *   link://   → Inline underlined link
 *
 * Action determines behavior:
 *   /navigate/PATH → Opens route in new tab
 *   /modal/NAME    → Opens modal dialog
 *   /source/DOC_ID?page=N → Opens source viewer modal
 */
interface CustomLinkProps {
    href?: string;
    children?: React.ReactNode;
    onNavigate?: (target: string) => void;
    onModal?: (target: string) => void;
    onSource?: (documentId: number, page?: number) => void;
}

const CustomLink = ({
    href,
    children,
    onNavigate,
    onModal,
    onSource,
}: CustomLinkProps) => {
    if (!href) {
        return <>{children}</>;
    }

    const parsed = parseLink(href);
    if (!parsed) {
        // Regular external link
        return (
            <Link href={href} target="_blank" rel="noopener noreferrer">
                {children}
            </Link>
        );
    }

    const { protocol, action, target, params } = parsed;

    // Determine click handler based on action
    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        if (action === "navigate" && onNavigate) {
            onNavigate(target);
        } else if (action === "modal" && onModal) {
            // Include query params for modals that need them (e.g., export-scope?ids=1,2,3)
            const targetWithParams =
                params && Object.keys(params).length
                    ? `${target}?${new URLSearchParams(params).toString()}`
                    : target;
            onModal(targetWithParams);
        } else if (action === "source" && onSource) {
            const documentId = parseInt(target, 10);
            if (!isNaN(documentId)) {
                const page = params?.page
                    ? parseInt(params.page, 10)
                    : undefined;
                onSource(documentId, isNaN(page as number) ? undefined : page);
            }
        }
    };

    // Render based on protocol
    if (protocol === "button") {
        // Icon varies by action type and target
        const getIcon = () => {
            if (action === "navigate") return <ExternalLink size={16} />;
            if (action === "source") return <FileText size={16} />;
            if (action === "modal") {
                // Extract modal name (before any query params)
                const modalName = target.split("?")[0];
                const config = MODAL_CONFIG[modalName];
                if (config) {
                    const Icon = config.icon;
                    return <Icon size={16} />;
                }
                // Fallback for unknown modals - log warning in dev
                if (process.env.NODE_ENV === "development") {
                    console.warn(
                        `[Markdown] Unknown modal "${modalName}" - add it to MODAL_CONFIG`
                    );
                }
                return <Plus size={16} />;
            }
            return null;
        };
        const icon = getIcon();

        return (
            <Box sx={{ display: "block", my: 1 }}>
                <Button
                    variant="contained"
                    color="secondary"
                    disableElevation
                    startIcon={icon}
                    onClick={handleClick}
                    sx={{
                        textTransform: "none",
                        borderRadius: 2,
                        padding: "10px 20px",
                        fontWeight: 500,
                        fontSize: "14px",
                    }}
                >
                    {children}
                </Button>
            </Box>
        );
    }

    // protocol === "link" - inline underlined link
    return (
        <Link
            component="button"
            onClick={handleClick}
            sx={{
                cursor: "pointer",
                textDecoration: "underline",
                color: "primary.main",
                "&:hover": {
                    color: "primary.dark",
                },
                // Reset button styles
                background: "none",
                border: "none",
                padding: 0,
                font: "inherit",
                textAlign: "inherit",
                verticalAlign: "baseline",
            }}
        >
            {children}
        </Link>
    );
};

const TABLE_BREAKOUT_CLASS = "markdown-table-breakout";

// Scrollable wrapper for tables — breaks out of the 900px centering wrapper to fill
// the full chat scroll container using CSS container queries.
// calc(-50cqw + 50%): "50%" = half of the centering wrapper's content width (constant
// at 434px once maxWidth:md is active); "-50cqw" pulls back to the container's left edge.
const TableWrapper = styled(Box)({
    overflowX: "auto",
    width: "100cqw",
    marginLeft: "calc(-50cqw + 50%)",
    padding: "8px 20px",
});

// Custom styled table components
const StyledTableInner = styled("table")(({ theme }) => ({
    borderCollapse: "separate",
    borderSpacing: 0,
    overflow: "hidden",
    width: "max-content",
    margin: "0 auto",
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[1],
    backgroundColor: theme.palette.background.paper,
}));

// Wrapper component that combines TableWrapper and StyledTableInner
const StyledTable = (props: React.HTMLAttributes<HTMLTableElement>) => (
    <TableWrapper className={TABLE_BREAKOUT_CLASS}>
        <StyledTableInner {...props} />
    </TableWrapper>
);

const StyledTableHead = styled("thead")(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "& a, & a:visited, & a:hover, & button, & button:hover": {
        color: `${theme.palette.primary.contrastText} !important`,
    },
}));

const StyledTableHeaderCell = styled("th")(({ theme }) => ({
    padding: theme.spacing(1.5, 2),
    textAlign: "left",
    fontWeight: 600,
    fontSize: theme.typography.body2.fontSize,
}));

const StyledTableRow = styled("tr")(({ theme }) => ({
    "tbody &": {
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    // Alternating row colors (zebra striping)
    "tbody &:nth-of-type(odd)": {
        backgroundColor: theme.palette.action.hover,
    },
}));

const StyledTableCell = styled("td")(({ theme }) => ({
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontSize: theme.typography.body2.fontSize,
    minWidth: 200,
    maxWidth: 400,
    verticalAlign: "top",
    "tbody tr:last-child &": {
        borderBottom: "none",
    },
}));

// Styled paragraph to remove default margins
const StyledParagraph = styled("p")({
    margin: 0,
    "&:not(:last-child)": {
        marginBottom: "0.75em",
    },
});

const MarkdownWrapper = styled(Box)({
    // Single-block: prose element is a direct child (markdown-to-jsx skips the wrapper div)
    // Multi-block: prose elements are children of the wrapper div
    [`& > *:not(div):not(.${TABLE_BREAKOUT_CLASS}), & > div > *:not(.${TABLE_BREAKOUT_CLASS})`]:
        {
            maxWidth: 800,
            marginLeft: "auto",
            marginRight: "auto",
        },
    // Tables are never constrained — they expand to the full MarkdownWrapper width
    [`& .${TABLE_BREAKOUT_CLASS}`]: {
        maxWidth: "none",
    },
    // Headings keep only spacing here. Sizes and weights come from the global
    // defaults in style.css; restating them in Emotion would land in
    // `@layer mui`, which outranks those defaults and would fork this surface
    // off the app-wide hierarchy again.
    "& :is(h1, h2)": {
        marginTop: "1em",
        marginBottom: "0.4em",
    },
    "& :is(h3, h4, h5, h6)": {
        marginTop: "0.8em",
        marginBottom: "0.3em",
    },
});

/**
 * Create markdown options with optional click handlers.
 *
 * Handlers:
 *   onNavigate - Called for /navigate/ actions (opens routes)
 *   onModal    - Called for /modal/ actions (opens dialogs)
 *   onSource   - Called for /source/ actions (opens document viewer)
 */
const createMarkdownOptions = (
    onNavigate?: (target: string) => void,
    onModal?: (target: string) => void,
    onSource?: (documentId: number, page?: number) => void
) => ({
    overrides: {
        p: {
            component: StyledParagraph,
        },
        table: {
            component: StyledTable,
        },
        thead: {
            component: StyledTableHead,
        },
        th: {
            component: StyledTableHeaderCell,
        },
        tr: {
            component: StyledTableRow,
        },
        td: {
            component: StyledTableCell,
        },
        a: {
            component: (props: React.ComponentProps<"a">) => (
                <CustomLink
                    {...props}
                    onNavigate={onNavigate}
                    onModal={onModal}
                    onSource={onSource}
                />
            ),
        },
    },
});

/**
 * Escape underscores in filenames and technical identifiers to prevent
 * them from being interpreted as emphasis markers.
 *
 * This handles:
 * - Filenames: contract_v2.pdf, report_2024.docx
 * - Technical identifiers: scope_id, user_name
 *
 * But preserves intentional emphasis like _this is italic_.
 */
const escapeUnderscoresInFilenames = (markdown: string): string => {
    // Pattern for common file extensions
    const fileExtensions =
        /\.(pdf|docx?|xlsx?|csv|txt|png|jpe?g|gif|json|xml|html?|md)(?=[\s,.)|\]]|$)/i;

    // Split by whitespace and process each "word"
    return markdown.replace(
        // Match words containing underscores that look like filenames or identifiers
        // This regex finds sequences without spaces that contain underscores
        /\S*_\S*/g,
        (match) => {
            // If it looks like a filename (has a file extension), escape underscores
            if (fileExtensions.test(match)) {
                return match.replace(/_/g, "\\_");
            }
            // If it's a technical identifier (snake_case with no spaces around underscores)
            // and doesn't look like intentional emphasis (_word_), escape it
            // Intentional emphasis has underscore at word boundary: _word_ or word _word_ word
            // Technical identifiers have underscores mid-word: some_identifier
            if (
                /^\w+_\w+/.test(match) &&
                !match.startsWith("_") &&
                !match.endsWith("_")
            ) {
                return match.replace(/_/g, "\\_");
            }
            return match;
        }
    );
};

/**
 * Replace a leading "---" thematic break with an HTML <hr> tag.
 *
 * markdown-to-jsx v9 has a YAML frontmatter detector that activates when
 * content starts with "---". If a second "---" appears later in the text
 * and the content between contains a colon, the library silently strips
 * everything between the two markers. LLM responses sometimes start with
 * a decorative "---", so converting it to an explicit <hr> prevents the
 * parser from entering frontmatter mode.
 */
const escapeLeadingThematicBreak = (markdown: string): string => {
    return markdown.replace(/^---+\s*\n/, "<hr />\n");
};

/**
 * Run all preprocessing steps on raw markdown before passing to the parser.
 */
/* eslint-disable react-refresh/only-export-components */
export const preprocessMarkdown = (markdown: string): string => {
    let result = markdown;
    result = escapeLeadingThematicBreak(result);
    result = escapeUnderscoresInFilenames(result);
    return result;
};

interface IMarkdownProps {
    children: string;
    /**
     * Callback for /navigate/ actions.
     * Called when a button://navigate/ or link://navigate/ link is clicked.
     * Receives the target path (e.g., "scopes", "comparisons/123").
     */
    onNavigate?: (target: string) => void;
    /**
     * Callback for /modal/ actions.
     * Called when a button://modal/ or link://modal/ link is clicked.
     * Receives the modal name (e.g., "create-scope").
     */
    onModal?: (target: string) => void;
    /**
     * Callback for /source/ actions.
     * Called when a button://source/ or link://source/ link is clicked.
     * Receives the document ID and optional page number.
     */
    onSource?: (documentId: number, page?: number) => void;
}

export const Markdown = ({
    children,
    onNavigate,
    onModal,
    onSource,
}: IMarkdownProps) => {
    const options = React.useMemo(
        () => createMarkdownOptions(onNavigate, onModal, onSource),
        [onNavigate, onModal, onSource]
    );

    const processedContent = React.useMemo(
        () => preprocessMarkdown(children),
        [children]
    );

    return (
        <MarkdownWrapper>
            <MarkdownComponent options={options}>
                {processedContent}
            </MarkdownComponent>
        </MarkdownWrapper>
    );
};
